import { useState, type ReactNode } from "react";
import { Platform, Pressable, View, Text } from "react-native";
import { MoonRadio } from "@louise/moon-ui";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { ChoiceGlassBackdrop } from "./OnboardingLayout";

type Props = {
  icon?: ReactNode;
  label: string;
  sublabel?: string;
  selected: boolean;
  onPress: () => void;
  layout?: "row" | "tile";
};

export default function OptionCard({
  icon,
  label,
  sublabel,
  selected,
  onPress,
  layout = "row",
}: Props) {
  const [hovered, setHovered] = useState(false);
  const isTile = layout === "tile";
  const capturePress = usePressCapture("OptionCard");

  // The label arrives already translated, so the funnel keys off the stable
  // control identifier rather than the visible copy. The radio beside the
  // card captures for itself — see below.
  const handlePress = () => {
    capturePress(pressLabel(label), "onboarding_option_card");
    onPress();
  };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${label} option`}
      accessibilityState={{ selected }}
      onPress={handlePress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={({ pressed }) => [
        isTile && { flex: 1 },
        {
          position: "relative",
          flexDirection: isTile ? "column" : "row",
          alignItems: "center",
          justifyContent: isTile ? "center" : "flex-start",
          aspectRatio: isTile ? 1 : undefined,
          minHeight: isTile ? 136 : undefined,
          paddingVertical: isTile ? 18 : 14,
          paddingHorizontal: isTile ? 18 : 16,
          gap: isTile ? 24 : 14,
          overflow: "hidden",
          borderRadius: isTile ? 18 : 0,
          borderWidth: isTile ? 1 : 0,
          borderColor: isTile
            ? selected
              ? "rgba(255, 244, 236, 0.34)"
              : hovered
                ? "rgba(255, 244, 236, 0.24)"
                : "rgba(255, 239, 225, 0.15)"
            : undefined,
          borderTopColor: isTile
            ? selected
              ? "rgba(255, 250, 246, 0.46)"
              : hovered
                ? "rgba(255, 248, 242, 0.38)"
                : "rgba(255, 244, 236, 0.28)"
            : undefined,
          borderBottomColor: isTile ? "rgba(255, 225, 207, 0.10)" : undefined,
          backgroundColor: isTile
            ? selected
              ? "rgba(255, 255, 255, 0.13)"
              : hovered
                ? "rgba(255, 255, 255, 0.075)"
                : "transparent"
            : selected
              ? "rgba(255, 255, 255, 0.13)"
              : hovered
                ? "rgba(255, 255, 255, 0.075)"
                : pressed
                  ? "rgba(255, 255, 255, 0.055)"
                  : "transparent",
          opacity: pressed ? 0.94 : 1,
          transform: [{ scale: pressed ? 0.985 : 1 }],
        },
        Platform.OS === "web" && {
          transition:
            "background-color 140ms ease, border-color 140ms ease, transform 120ms ease, opacity 120ms ease",
        },
      ]}
    >
      {isTile && (hovered || selected) && <ChoiceGlassBackdrop />}
      {icon && <View style={{ zIndex: 1 }}>{icon}</View>}
      <View
        style={{
          flex: isTile ? undefined : 1,
          zIndex: 1,
          alignItems: isTile ? "center" : "flex-start",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: isTile ? 18 : 15,
            fontWeight: "500",
            color: "rgba(255, 255, 255, 0.94)",
            textAlign: isTile ? "center" : "left",
          }}
        >
          {label}
        </Text>
        {sublabel && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: "rgba(255, 255, 255, 0.64)",
              marginTop: isTile ? 5 : 2,
              lineHeight: isTile ? 17 : undefined,
            }}
          >
            {sublabel}
          </Text>
        )}
      </View>
      {!isTile && (
        <MoonRadio
          value={selected}
          // The radio captures for itself, so it takes the raw handler and
          // the shared identifier — routing it through `handlePress` would
          // count one press twice.
          onChange={onPress}
          action="onboarding_option_card"
          accentColor="#F2B48B"
        />
      )}
    </Pressable>
  );
}
